import { createCalendarService, DateStringFormatter, StructuredDate, toStructuredDate } from '../services';
import { getDefaultLocalization } from './helpers';

const DATE_SPLITTING_REGEX = /[ .:;?!~,`"&|()<>{}[\]\r\n\-/\\]+/;

enum DatePart {
    Day = 'd',
    Month = 'm',
    Year = 'y',
}

type DatePartDictionary = {
    [P in DatePart]: number;
};

const fromDateString = (dateString: string): StructuredDate => {
    const dateValuesArray = dateString.split(DATE_SPLITTING_REGEX);
    const dateFormatsArray = getDefaultLocalization().dateFormat.toLowerCase().split(DATE_SPLITTING_REGEX);
    const formatIndexesDictionary = {} as DatePartDictionary;

    dateFormatsArray.forEach((element: string, index: number) => {
        formatIndexesDictionary[element[0] as DatePart] = Number(index);
    });

    return toStructuredDate(
        new Date(
            Date.UTC(
                Number(dateValuesArray[formatIndexesDictionary[DatePart.Year]]),
                Number(dateValuesArray[formatIndexesDictionary[DatePart.Month]]) - 1 || 0,
                Number(dateValuesArray[formatIndexesDictionary[DatePart.Day]])
            )
        )
    );
};

const getCurrentDate = (): StructuredDate => toStructuredDate(new Date());

const isValidDateString = (dateString: string): boolean => {
    const dateValuesArray = dateString.split(DATE_SPLITTING_REGEX);
    return dateValuesArray.length === 3 && !Number.isNaN(Date.parse(dateString));
};

const toDate = ({ year = 2018, month = 0, date = 1 }: StructuredDate): Date =>
    new Date(Date.UTC(year, month, date, 0, 0, 0));

const getDateString = (date: StructuredDate): string =>
    toDate(date).toLocaleDateString(getDefaultLocalization().cultureCode, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });

const parseDateFromFormat = (dateString: string): DateStringFormatter => {
    if (isValidDateString(dateString)) {
        return {
            isValid: true,
            toFormat: () => getDateString(fromDateString(dateString)),
        };
    }

    const dateValuesArray = dateString.split(DATE_SPLITTING_REGEX);
    // If we only have month and year (length is 2), add day = 1 to complete the date
    if (dateValuesArray.length === 2) {
        const month = parseInt(dateValuesArray[0], 10);
        const year = parseInt(dateValuesArray[1], 10);

        // If valid month and year
        if (!Number.isNaN(month) && !Number.isNaN(year) && month >= 1 && month <= 12) {
            // Create a date for the first day of the month
            const date = new Date(Date.UTC(year, month - 1, 1));

            if (!Number.isNaN(date.getTime())) {
                const completeDate = toStructuredDate(date);
                return {
                    isValid: true,
                    toFormat: () => getDateString(completeDate),
                };
            }
        }
    }

    return {
        isValid: false,
        toFormat: () => '',
    };
};

export default createCalendarService({
    getLocalization: getDefaultLocalization,
    isValidDateString,
    getCurrentDate,
    fromDateString,
    getDateString,
    parseDateFromFormat,
});
